import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, CopyField, Switch, TableSkeleton } from "../../../components/ui";
import { getSiteMaintenance, updateSiteMaintenance } from "../../../services/maintenance";

const errorMessage = (error) => error?.response?.data?.message || error?.message || "";

// <input type="datetime-local"> works in local time
const toLocalInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/** Switches afomedia.com to its maintenance page (the app and the API keep working). */
const SiteMaintenanceCard = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [message, setMessage] = useState("");
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const apply = (data) => {
    setState(data);
    setMessage(data.message || "");
    setUntil(toLocalInput(data.until));
  };

  // Loaded once (`t` is not a dependency: it may change on every render)
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    getSiteMaintenance()
      .then(apply)
      .catch((error) => toast.error(errorMessage(error) || tRef.current("Error fetching data")));
  }, []);

  const save = (changes, done) => {
    setSaving(true);
    return updateSiteMaintenance({ message, until: until ? new Date(until).toISOString() : null, ...changes })
      .then((data) => {
        apply(data);
        toast.success(done);
      })
      .catch((error) => toast.error(errorMessage(error) || t("Error updating data")))
      .finally(() => setSaving(false));
  };

  if (!state) {
    return (
      <section className="ui-card maint-site">
        <TableSkeleton rows={2} />
      </section>
    );
  }

  const dirty = message !== (state.message || "") || until !== toLocalInput(state.until);

  return (
    <section className="ui-card maint-site" data-on={state.enabled ? "" : undefined}>
      <div className="maint-task-head">
        <h3>{t("Website in maintenance")}</h3>
        {state.enabled ? (
          <Badge color="amber" dot>
            {t("Maintenance page shown")}
          </Badge>
        ) : (
          <Badge color="green" dot>
            {t("Website online")}
          </Badge>
        )}
      </div>
      <p className="maint-task-text">
        {t("Every visitor of afomedia.com sees the maintenance page. The app and the API keep working. Takes effect within 30 seconds.")}
      </p>

      <Switch
        checked={state.enabled}
        label={t("Website in maintenance")}
        onLabel={t("Maintenance on")}
        offLabel={t("Maintenance off")}
        onChange={(enabled) => setConfirming(enabled ? "on" : "off")}
      />
      {state.enabled && state.since && (
        <p className="ui-hint">
          {t("Since {{date}}", { date: new Date(state.since).toLocaleString() })}
          {state.by ? ` · ${state.by}` : ""}
        </p>
      )}

      <div className="maint-site-fields">
        <div className="ui-field">
          <label htmlFor="site-maint-message">{t("Message to visitors")}</label>
          <textarea
            id="site-maint-message"
            className="ui-input"
            rows={3}
            maxLength={500}
            value={message}
            placeholder={state.defaultMessage}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div className="ui-field">
          <label htmlFor="site-maint-until">{t("Expected return (optional)")}</label>
          <input id="site-maint-until" className="ui-input" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
        </div>
      </div>
      {dirty && (
        <div className="maint-task-actions">
          <button type="button" className="ui-btn ui-btn-secondary" disabled={saving} onClick={() => save({}, t("Saved"))}>
            {t("Save the message")}
          </button>
        </div>
      )}

      {state.enabled && state.previewUrl && (
        <>
          <CopyField id="site-maint-link" label={t("Team link (shows the site despite the maintenance, for 12 hours)")} value={state.previewUrl} />
          <p className="ui-hint">{t("Share it only with the team. A new link is created each time the maintenance is switched on.")}</p>
        </>
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={() =>
          save({ enabled: confirming === "on" }, confirming === "on" ? t("The website is in maintenance") : t("The website is back online"))
        }
        confirmLabel={confirming === "on" ? t("Switch on the maintenance") : t("Put the website back online")}
        danger={confirming === "on"}
      >
        <p>
          {confirming === "on"
            ? t("Show the maintenance page to every visitor of the website?")
            : t("Put the website back online for everyone?")}
        </p>
      </ConfirmDialog>
    </section>
  );
};

export default SiteMaintenanceCard;
